import {
  AppStore,
  AppStorePluginProduct,
  AppStorePluginStore,
} from '@/services/cordova.purchase.types'
import { Plan, BillingPlans, DiscountType, BillingPlanID } from './interfaces'
import { Sentry } from '@/services/sentry'

export interface AmountPerPeriod {
  /** Plan's price divided by the number of billing periods. */
  perMonth: number
  /** `perMonth`'s value multiplied by 12 months. */
  perYear: number
  /** The default value (eg. `perYear` for annual and `perMonth` for monthly). */
  perPeriod: number
}

export interface PricePerPeriod {
  /** Plan's price divided by the number of billing periods. */
  perMonth: string
  /** `perMonth`'s value multiplied by 12 months. */
  perYear: string
  /** The default value (eg. `perYear` for annual and `perMonth` for monthly). */
  perPeriod: string
}

/**
 * Strings representations of a plan's price.
 */
export class PlanPrice implements PricePerPeriod {
  private _defaultPeriod: 'perMonth' | 'perYear'
  private _format: (price: number, forceMinimumDigits?: boolean) => string

  perMonth: string
  perYear: string
  perPeriod: string

  constructor(
    pricePerPeriod: AmountPerPeriod,
    defaultPeriod: 'perMonth' | 'perYear',
    formatMethod: (price: number, forceMinimumDigits?: boolean) => string,
  ) {
    this._defaultPeriod = defaultPeriod
    this._format = formatMethod

    this.perMonth = this._format(pricePerPeriod.perMonth)
    this.perYear = this._format(pricePerPeriod.perYear, false)
    this.perPeriod = this[this._defaultPeriod]
  }

  /**
   * The price with a label representing the billing period.
   *
   * @example
   * PlanPrice.withPeriod.perMonth === '$24.00/month'
   * PlanPrice.withPeriod.perYear === '$288 ($24.00/month)'
   */
  get withPeriod(): PricePerPeriod {
    const values = {
      perMonth: `${this.perMonth}/month`,
      perYear: `${this.perYear} (${this.perMonth}/month)`,
    }

    return {
      ...values,
      perPeriod: values[this._defaultPeriod],
    }
  }
}

class PlanDiscount {
  private _planAmount: number

  type: DiscountType
  value: number

  constructor(_planAmount: number, type: DiscountType, value: number) {
    this._planAmount = _planAmount
    this.type = type
    this.value = value
  }

  /**
   * Get the plan's amount with the discount applied.
   */
  get amount(): number {
    switch (this.type) {
      case 'percentage':
        return this._planAmount * (1 - this.value / 100)
      case 'fixed_amount':
        return this._planAmount - this.value
    }
  }

  /**
   * Create a new instance of PlanDiscount if `type` and `value` are not `null`.
   */
  static maybeCreate(
    planAmount: number,
    type: DiscountType | null,
    value: number | null,
  ): PlanDiscount | undefined {
    if (!type || !value) {
      return
    }

    return new PlanDiscount(planAmount, type, value)
  }
}

/**
 * Base class for billing plans.
 */
export abstract class PlanView implements Plan {
  /**
   * The plan's id - 'plan_free', 'plan_monthly', 'plan_annual'
   * we use these on the backend and on Stripe.
   */
  abstract id: BillingPlanID
  /** The plan's human readable name ('Free', 'Monthly', 'Annual'). */
  abstract nickname: string
  /** The duration of each billing period (in months). */
  abstract periodDuration: 1 | 12
  /** The default value to get from `PricePerPeriod` objects. */
  abstract defaultPeriod: 'perMonth' | 'perYear'
  /** A dictionary of strings representing the plan's billing frequency. */
  abstract labels: {
    period: string
    basis: string
    frequency: string
  }

  /** The amount charged per billing period (cents, integer). */
  public amount: number
  /** The discount applied to this plan. */
  public discount?: PlanDiscount
  /** The billing currency (ISO code, eg. 'USD'). */
  public currency: string

  /** Plan's price in cents with discount applied, normalized to monthly and yearly values. */
  private _normalizedAmount?: AmountPerPeriod
  /** Plan's price in cents without discounts, normalized to monthly and yearly values. */
  private _normalizedAmountNoDiscount?: AmountPerPeriod

  /** String representations of the plan's price with discounts applied. */
  private _price?: PlanPrice
  /** String representations of the plan's price without discounts. */
  private _priceNoDiscount?: PlanPrice

  constructor(
    amount: number,
    discount?: PlanDiscount,
    currency: string = 'USD',
  ) {
    this.amount = amount
    this.discount = discount
    this.currency = currency
  }

  /**
   * Get the plan's amount with the discount applied.
   *
   * If there's no discount, returns the regular amount.
   */
  get discountedAmount(): number {
    if (!this.discount) {
      return this.amount
    }

    return this.discount.amount
  }

  /**
   * Plan's amount (price in cents) with discount applied, normalized to monthly and yearly values.
   *
   * @example
   * // An annual plan billed at $100 per year with a 25% discount:
   * {
   *   perYear: 7500, // $75
   *   perMonth: 625, // $6.25
   *   perPeriod: 7500 // Same as `perYear`.
   * }
   */
  get normalizedAmount(): AmountPerPeriod {
    if (!this._normalizedAmount) {
      this._normalizedAmount = this.normalize(this.discountedAmount)
    }
    return this._normalizedAmount
  }

  /**
   * Plan's price in cents without discounts, normalized to monthly and yearly values.
   *
   * @example
   * // An annual plan billed at $100 per year with a 25% discount:
   * {
   *   perYear: 10000, // $100
   *   perMonth: 833.33, // $8.33
   *   perPeriod: 10000 // Same as `perYear`.
   * }
   */
  get normalizedAmountNoDiscount(): AmountPerPeriod {
    if (!this._normalizedAmountNoDiscount) {
      this._normalizedAmountNoDiscount = this.normalize(this.amount)
    }
    return this._normalizedAmountNoDiscount
  }

  /**
   * String representations of the plan's price with discounts applied.
   */
  get price(): PlanPrice {
    if (!this._price) {
      this._price = new PlanPrice(
        this.normalizedAmount,
        this.defaultPeriod,
        this.formatPrice.bind(this),
      )
    }
    return this._price
  }

  /**
   * String representations of the plan's price with no discounts.
   */
  get priceNoDiscount(): PlanPrice {
    if (!this._priceNoDiscount) {
      this._priceNoDiscount = new PlanPrice(
        this.normalizedAmountNoDiscount,
        this.defaultPeriod,
        this.formatPrice.bind(this),
      )
    }
    return this._priceNoDiscount
  }

  /**
   * Format the given price using this plan's currency.
   *
   * @param price Price in cents
   * @param forceMinimumDigits Force the formatted value
   * to always have a minimum of 2 digits. Defaults to `true`.
   *
   * @example
   * // this.currency = 'USD'
   * this.formatPrice(20099) // '$200.99'.
   * this.formatPrice(20000, false) // '$200'.
   *
   * // this.currency = 'EUR'
   * this.formatPrice(20099) // '€200.99'.
   * this.formatPrice(20000, false) // '€200'.
   */
  formatPrice(price: number, forceMinimumDigits: boolean = true): string {
    // Force show 2 digits if we have cents.
    const hasCents = price % 100 !== 0

    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: this.currency,
      minimumFractionDigits: forceMinimumDigits || hasCents ? 2 : 0,
      maximumFractionDigits: 2,
    })

    return formatter.format(price / 100)
  }

  /**
   * Normalize the given `amount` to monthly and yearly values.
   *
   * - `perMonth`: The amount divided by the number of billing periods.
   * - `perYear`: `perMonth`'s value multiplied by 12 months.
   * - `perPeriod`: The default value (eg. `perYear` for annual and `perMonth` for monthly).
   *
   * @example
   * // this.periodDuration = 12
   * // this.normalize(10000):
   * {
   *   perYear: 10000,
   *   perMonth: 833.33,
   *   perPeriod: 10000 // Same as `perYear`.
   * }
   */
  private normalize(amount: number): AmountPerPeriod {
    const values = {
      perMonth: amount / this.periodDuration,
      perYear: (amount / this.periodDuration) * 12,
    }

    return {
      ...values,
      perPeriod: values[this.defaultPeriod],
    }
  }
}

/**
 * Free billing plan.
 */
export class FreePlanView extends PlanView {
  public id = 'plan_free' as const
  public nickname = 'Free'
  public periodDuration = 12 as const
  public defaultPeriod = 'perYear' as const

  public labels = {
    period: 'year',
    basis: 'an annual',
    frequency: 'annually',
  } as const

  constructor(billing_plans: BillingPlans) {
    const amount = billing_plans.plan_free
    super(amount)
  }
}

/**
 * Monthly billing plan.
 */
export class MonthlyPlanView extends PlanView {
  public id = 'plan_monthly' as const
  public nickname = 'Monthly'
  public periodDuration = 1 as const
  public defaultPeriod = 'perMonth' as const

  public labels = {
    period: 'month',
    basis: 'a monthly',
    frequency: 'monthly',
  } as const

  constructor(
    amount: number,
    discount?: PlanDiscount,
    currency: string = 'USD',
  ) {
    super(amount, discount, currency)
  }

  static createView(billingPlans: BillingPlans): MonthlyPlanView {
    const discount = PlanDiscount.maybeCreate(
      billingPlans.plan_monthly,
      billingPlans.discount.type_monthly,
      billingPlans.discount.value_monthly,
    )
    return new MonthlyPlanView(billingPlans.plan_monthly, discount)
  }

  static createAppStoreView(
    appStore: AppStore,
    platform: 'ios' | 'android',
  ): MonthlyPlanView {
    const fallbackAmount = 2399
    const { currency, amount } = new AppStoreData(
      appStore,
      appStore.monthly,
      platform,
      fallbackAmount,
    )
    return new MonthlyPlanView(amount, undefined, currency)
  }
}

/**
 * Annual billing plan.
 */
export class AnnualPlanView extends PlanView {
  public id = 'plan_annual' as const
  public nickname = 'Annual'
  public periodDuration = 12 as const
  public defaultPeriod = 'perYear' as const

  public labels = {
    period: 'year',
    basis: 'an annual',
    frequency: 'annually',
  } as const

  constructor(
    amount: number,
    discount?: PlanDiscount,
    currency: string = 'USD',
  ) {
    super(amount, discount, currency)
  }

  static createView(billingPlans: BillingPlans): AnnualPlanView {
    const discount = PlanDiscount.maybeCreate(
      billingPlans.plan_annual,
      billingPlans.discount.type_annual,
      billingPlans.discount.value_annual,
    )
    return new AnnualPlanView(billingPlans.plan_annual, discount)
  }

  static createAppStoreView(
    appStore: AppStore,
    platform: 'ios' | 'android',
  ): AnnualPlanView {
    const fallbackAmount = 19499
    const { currency, amount } = new AppStoreData(
      appStore,
      appStore.annual,
      platform,
      fallbackAmount,
    )
    return new AnnualPlanView(amount, undefined, currency)
  }
}

/**
 * A helper class to get the plan data from mobile AppStores.
 */
class AppStoreData {
  private store: AppStorePluginStore
  private plan: AppStorePluginProduct | undefined
  private platform?: 'ios' | 'android'
  private fallbackAmount: number

  constructor(
    appStore: AppStore,
    plan: AppStorePluginProduct | undefined,
    platform: 'ios' | 'android',
    fallbackAmount: number,
  ) {
    if (!appStore.store) {
      throw new Error('appStore.store is not initialized.')
    }

    if (platform === 'ios' && !plan) {
      throw new Error(`No plan in apple data.`)
    }

    this.plan = plan
    this.store = appStore.store
    this.platform = platform
    this.fallbackAmount = fallbackAmount
  }

  /**
   * Get the currency for the given `appStore` plan.
   */
  get currency(): string {
    if (!this.plan) {
      return 'USD'
    }

    // For cordova-plugin-purchase version
    // 11: `currency` is a direct property of the plan.
    // 13: `currency` is a property of PricingPhase.
    const currency =
      'offers' in this.plan
        ? this.getLastPricingPhase(this.plan)?.currency
        : this.plan.currency

    return currency || 'USD'
  }

  /**
   * Get the plan's price in cents.
   *
   * On Android, may fall back to `fallbackPrice` if Google billing is unavailable, in
   * which case no price is returned by Google.
   *
   * On iOS, it is expected that a price is always present, so an error is thrown in case
   * it is not.
   */
  get amount(): number {
    const plan = this.plan

    // First, we try to get the price of the plan. The method depends on which
    // version of cordova-plugin-purchase the mobile app uses, so we switch based
    // on the type of the plan.
    //
    // TypeScript does not like it when we try to use `instanceof
    // IapStore.IStoreProduct`, which seems to be some problem with IapStore
    // being a namespace:
    // >> Cannot use namespace 'IapStore' as a value.
    //
    // So instead, we check for the existence of the properties we access.
    if (plan) {
      if ('priceMicros' in plan && plan.priceMicros) {
        // Version 11:
        return plan.priceMicros / 10000
      } else if ('offers' in plan && plan.offers.length > 0) {
        // Version 13:
        // https://github.com/j3k0/cordova-plugin-purchase/blob/master/api/classes/CdvPurchase.Offer.md
        const lastPhase = this.getLastPricingPhase(plan)
        if (lastPhase && lastPhase.priceMicros > 0) {
          return lastPhase.priceMicros / 10000
        }
        // At this point there are no pricing phases with a valid price; either
        // there are no pricing phases or no pricing phases with a non-zero
        // price.
      }
    }

    // If we reach this point, there is no valid price in `plan`. How we handle
    // this depends on platform.
    if (this.platform === 'ios') {
      // With Apple billing, we always expect a price to be present. A Sentry error
      // gets logged further up the call chain.
      const message = `Annual plan in Apple data has no price.`
      this.setAppStoreDataSentryContext()
      throw new Error(message)
    }
    // With Google billing it can happen that no price (and no other subscription
    // details) get returned for various reasons. For example: the user is not signed
    // in into a Google account on the device, or the device has an old version of
    // Google Play.
    // This is not an error case. We fall back to displaying a hard-coded price in USD.
    // We still log a message to Sentry though, to see how often this happens.
    const message = `Annual plan in Google data has no price. Showing hard-coded price as fallback.`
    this.setAppStoreDataSentryContext()
    Sentry.captureMessage(message)
    return this.fallbackAmount
  }

  /**
   * Add app store data to Sentry context for debugging. We only add the context here,
   * a Sentry error gets logged later, further up in the call chain.
   *
   * The data is added in JSON stringified form to ensure all data is displayed in
   * Sentry in its raw form.
   */
  private setAppStoreDataSentryContext(): void {
    let allPlansData = {}

    try {
      // Using internal property of store that contains all loaded products.
      if ('products' in this.store) {
        allPlansData = { all_plans_data: JSON.stringify(this.store.products) }
      }
    } catch (e) {
      // Ignore stringify error
    }

    const context = {
      plan: JSON.stringify(this.plan),
      ...allPlansData,
    }

    if (Sentry.setContext) {
      Sentry.setContext('app_store_data', context)
    }
  }

  /**
   * Get the last pricing phase for this product. Returns undefined if it doesn't exist.
   *
   * On iOS, there are two pricing phases: one for the free trial and one
   * for the actual subscription.
   * On Android, there is only one pricing phase: the actual subscription.
   * So to get the "true" subscription pricing, we want to get the last pricing
   * phase.
   */
  private getLastPricingPhase(
    product: CdvPurchase.Product,
  ): CdvPurchase.PricingPhase | undefined {
    if (product.offers.length > 0) {
      const pricingPhases = product.offers[0].pricingPhases
      if (pricingPhases.length > 0) {
        return pricingPhases[pricingPhases.length - 1]
      }
    }
    return undefined
  }
}
